import React from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Star, Shield, Clock, MapPin, Building, Store, Coffee, Briefcase, ChevronDown, Plus, Play, ArrowRight } from 'lucide-react';

import Hero from '../components/Hero';
import Services from '../components/Services';
import SEO from '../components/SEO';

const Home = () => {
  // Global scroll trackers
  const { scrollYProgress } = useScroll();

  // Smooth out the progress bar animation
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  // Transform values for massive background typography
  const textMoveLeft = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const textMoveRight = useTransform(scrollYProgress, [0, 1], ["-30%", "0%"]);

  // Parallax float for small accent shapes
  const floatUp = useTransform(scrollYProgress, [0, 1], ["0%", "-500%"]);
  const videoParallax = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  const marqueeVariants = {
    animate: {
      x: [0, -1035],
      transition: { x: { repeat: Infinity, repeatType: "loop", duration: 15, ease: "linear" } },
    },
  };

  const slowMarqueeVariants = {
    animate: {
      x: [0, -1000],
      transition: { x: { repeat: Infinity, repeatType: "loop", duration: 30, ease: "linear" } },
    }
  };

  return (
    <>
      <SEO
        title="A2Z Signs | Custom Business Signs & Commercial Signage USA"
        description="A2Z Signs delivers custom business signs, monument signs, channel letters, vehicle graphics & large format printing across the USA. Based in Upland, CA. Get a free quote today!"
        keyword="custom signs company USA"
      />

      <div className="w-full bg-brand-light overflow-hidden">

        {/* 1. Global Scroll Progress Bar */}
        <motion.div
          className="fixed top-0 left-0 right-0 h-1.5 bg-brand-cyan origin-left z-[100] shadow-[0_0_10px_rgba(0,198,255,0.8)]"
          style={{ scaleX }}
        />

        <div className="relative">
          <Hero />

          {/* Animated Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="absolute bottom-32 left-1/2 transform -translate-x-1/2 z-30 flex flex-col items-center text-white/50 hidden md:flex"
          >
            {/* <span className="text-xs uppercase tracking-widest mb-2 font-bold">Scroll</span> */}
            <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
              <ChevronDown size={24} />
            </motion.div>
          </motion.div>
        </div>

        {/* 2. Overlapping Trust / Stats Banner */}
        <section className="relative z-30 -mt-24 container mx-auto px-6 md:px-12 mb-16">
          <div className="bg-white/90 backdrop-blur-xl rounded-[2.5rem] shadow-[0_20px_60px_rgba(11,25,143,0.1)] border border-white p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 divide-y md:divide-y-0 md:divide-x divide-gray-200">
              {[
                { icon: <Clock size={36} />, stat: "10+ Years", label: "Industry Experience" },
                { icon: <Shield size={36} />, stat: "100%", label: "Satisfaction Guarantee" },
                { icon: <Star size={36} />, stat: "Premium", label: "Quality Materials" },
                { icon: <MapPin size={36} />, stat: "Upland, CA", label: "Local Fabrication" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex flex-col items-center text-center pt-8 md:pt-0 group cursor-default"
                >
                  <div className="w-16 h-16 bg-brand-light text-brand-blue rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-cyan group-hover:text-white transition-colors duration-300 shadow-inner">
                    {item.icon}
                  </div>
                  <h3 className="text-3xl font-heading font-black text-brand-dark mb-1">{item.stat}</h3>
                  <p className="text-gray-500 font-bold uppercase tracking-wider text-xs">{item.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 3. NEW: "Trusted By" Client Logo Marquee */}


        {/* 4. Tilted Infinite Marquee */}
        <div className="relative my-24 py-10 transform -skew-y-2 bg-brand-cyan overflow-hidden shadow-2xl z-10 border-y-8 border-brand-dark">
          <motion.div
            className="flex font-heading font-black text-4xl text-brand-dark uppercase tracking-widest items-center skew-y-2"
            variants={marqueeVariants}
            animate="animate"
          >
            {[...Array(4)].map((_, i) => (
              <span key={i} className="flex items-center">
                <span className="mx-12 opacity-50">•</span> CUSTOM SIGNS
                <span className="mx-12 opacity-50">•</span> ILLUMINATED
                <span className="mx-12 opacity-50">•</span> MONUMENT SIGNS
                <span className="mx-12 opacity-50">•</span> VEHICLE WRAPS
              </span>
            ))}
          </motion.div>
        </div>

        {/* 5. Services Grid */}
        <div className="relative z-20">
          <Services />
        </div>

        {/* 6. NEW: "Inside the Workshop" Cinematic Video Block */}
        <section className="relative w-full h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden bg-brand-dark">
          <motion.div
            style={{ y: videoParallax }}
            className="absolute inset-0 bg-[url('https://picsum.photos/seed/workshop/1920/1080')] bg-cover bg-center opacity-40"
          ></motion.div>
          <div className="absolute inset-0 bg-brand-dark/50 z-10"></div>

          <div className="relative z-20 text-center px-6">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              className="w-24 h-24 mx-auto bg-brand-cyan/20 rounded-full flex items-center justify-center mb-8 cursor-pointer group hover:bg-brand-cyan/40 transition-all backdrop-blur-md border border-brand-cyan/50"
            >
              <div className="w-16 h-16 bg-brand-cyan rounded-full flex items-center justify-center text-brand-dark group-hover:scale-110 transition-transform shadow-[0_0_20px_rgba(0,198,255,0.8)]">
                <Play size={28} className="ml-1" />
              </div>
            </motion.div>
            <h2 className="text-4xl md:text-6xl font-heading font-black text-white mb-4">Inside the <span className="text-brand-cyan">Workshop</span></h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">At A2Z Signs, we turn raw materials into powerful, impossible-to-ignore custom signage. Inside our Upland workshop, we combine expert craftsmanship with cutting-edge technology to build bold LED signs, illuminated displays, and high-impact signage that makes your brand dominate every road.</p>
          </div>
        </section>

        {/* 7. Glassmorphism Industries Section with Scroll Typography */}
        <section className="py-32 bg-brand-dark text-white relative overflow-hidden">
          <motion.div style={{ x: textMoveLeft }} className="absolute top-20 left-0 whitespace-nowrap text-[12rem] font-heading font-black text-white/[0.03] pointer-events-none select-none z-0">
            A2Z SIGNS • UPLAND CA • A2Z SIGNS • UPLAND CA
          </motion.div>
          <motion.div style={{ x: textMoveRight }} className="absolute bottom-20 left-0 whitespace-nowrap text-[12rem] font-heading font-black text-brand-cyan/[0.03] pointer-events-none select-none z-0">
            IMPACT • VISIBILITY • IMPACT • VISIBILITY
          </motion.div>

          <motion.div style={{ y: floatUp }} className="absolute top-full left-20 text-brand-cyan/20 z-0">
            <Plus size={60} />
          </motion.div>

          <div className="container mx-auto px-6 md:px-12 relative z-10">
            <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
              <div className="max-w-2xl">
                <h2 className="text-5xl md:text-6xl font-heading font-black mb-6 leading-tight">
                  Industries <br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-cyan to-blue-400">We Elevate.</span>
                </h2>
                <p className="text-xl text-gray-400 font-light">
                  At A2Z Signs, we elevate every industry with bold, custom-crafted signage solutions. From retail stores and restaurants to real estate and corporate offices, we design and fabricate high-impact signs that strengthen brand identity, attract more customers, and drive business growth across every sector.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { icon: <Store size={40} />, title: "Retail", desc: "Attract foot traffic with illuminated letters and window graphics." },
                { icon: <Building size={40} />, title: "Corporate", desc: "Establish authority with professional lobby signs and monuments." },
                { icon: <Coffee size={40} />, title: "Restaurants", desc: "Set the mood with custom neon signs and digital menu boards." },
                { icon: <Briefcase size={40} />, title: "Real Estate", desc: "Guide visitors seamlessly with directional signs and site markers." }
              ].map((industry, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/5 backdrop-blur-xl border border-white/10 p-10 rounded-[2rem] hover:bg-white/10 hover:border-brand-cyan/50 hover:-translate-y-3 transition-all duration-500 group shadow-2xl relative overflow-hidden"
                >
                  <div className="absolute -right-10 -top-10 w-32 h-32 bg-brand-cyan opacity-0 group-hover:opacity-20 rounded-full blur-[40px] transition-opacity duration-500"></div>
                  <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-brand-cyan mb-8 group-hover:scale-110 group-hover:bg-brand-cyan group-hover:text-brand-dark transition-all duration-500 relative z-10">
                    {industry.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-brand-cyan transition-colors relative z-10">{industry.title}</h3>
                  <p className="text-gray-400 leading-relaxed relative z-10">{industry.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 8. NEW: Interactive Process Timeline */}
        <section className="py-32 bg-brand-light relative">
          <div className="container mx-auto px-6 md:px-12 max-w-5xl">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-5xl font-heading font-black text-brand-dark mb-4">How We Work</h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-brand-blue to-brand-cyan mx-auto rounded-full"></div>
            </div>

            <div className="relative border-l-4 border-brand-cyan/20 ml-6 md:ml-12 space-y-16 py-8">
              {[
                { step: "01", title: "Consultation & Concept", text: "We discuss your vision, survey the installation site, and provide tailored recommendations." },
                { step: "02", title: "Design & Permitting", text: "Our designers create detailed proofs while our team handles all local city permitting." },
                { step: "03", title: "Custom Fabrication", text: "Your sign is built in-house using premium materials for maximum durability and impact." },
                { step: "04", title: "Expert Installation", text: "Our licensed technicians ensure a flawless, secure, and code-compliant installation." }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="relative pl-12 md:pl-20"
                >
                  {/* Glowing Dot on the timeline */}
                  <div className="absolute -left-[14px] top-2 w-6 h-6 bg-brand-cyan rounded-full border-4 border-brand-light shadow-[0_0_15px_rgba(0,198,255,0.6)]"></div>

                  <h4 className="text-brand-cyan font-bold text-xl mb-2 flex items-center gap-3">
                    Step {item.step} <div className="w-10 h-0.5 bg-brand-cyan/30"></div>
                  </h4>
                  <h3 className="text-3xl font-heading font-bold text-brand-dark mb-4">{item.title}</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 9. Creative Testimonials */}
        <section className="py-32 bg-white relative overflow-hidden">
          <div className="absolute top-10 left-10 text-[20rem] font-serif text-gray-50 leading-none select-none z-0">"</div>
          <div className="container mx-auto px-6 md:px-12 relative z-10">
            <div className="text-center mb-20">
              <h2 className="text-5xl font-heading font-black text-brand-dark mb-6">Word on the <span className="text-brand-cyan">Street</span></h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-brand-blue to-brand-cyan mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {[
                { text: "A2Z Signs completely transformed our storefront. The channel letters are perfectly bright.", author: "Sarah Jenkins", role: "Boutique Owner", mt: "mt-0" },
                { text: "We needed a large monument sign. They handled everything from permits to the final build flawlessly.", author: "Mark D.", role: "Property Manager", mt: "md:mt-12" },
                { text: "The fleet wraps look amazing. Our brand visibility has skyrocketed since we started driving.", author: "David Reyes", role: "Logistics", mt: "md:mt-24" }
              ].map((testimonial, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.2 }}
                  className={`bg-brand-light p-10 rounded-[2rem] shadow-[0_10px_40px_rgba(0,0,0,0.04)] hover:shadow-xl transition-shadow ${testimonial.mt} relative`}
                >
                  <div className="text-brand-cyan mb-8 flex gap-1">
                    {[...Array(5)].map((_, star) => <Star key={star} fill="currentColor" size={24} />)}
                  </div>
                  <p className="text-gray-700 text-xl font-medium leading-relaxed mb-10">"{testimonial.text}"</p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-brand-blue rounded-full flex items-center justify-center text-white font-bold text-xl">
                      {testimonial.author.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-brand-dark text-lg">{testimonial.author}</p>
                      <p className="text-brand-cyan text-sm font-bold uppercase tracking-wider">{testimonial.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 10. Massive Final Pre-Footer CTA */}
        <section className="py-24 bg-brand-light relative">
          <div className="container mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-brand-blue to-brand-dark rounded-[3rem] p-12 md:p-20 text-center relative overflow-hidden shadow-2xl border border-brand-cyan/20"
            >
              <div className="absolute top-0 right-0 w-96 h-96 bg-brand-cyan opacity-20 rounded-full blur-[100px] -mr-20 -mt-20"></div>
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-white opacity-10 rounded-full blur-[100px] -ml-20 -mb-20"></div>

              <div className="relative z-10 max-w-3xl mx-auto">
                <h2 className="text-5xl md:text-6xl font-heading font-black text-white mb-8">
                  Ready to Upgrade Your <span className="text-brand-cyan">Visibility?</span>
                </h2>
                <p className="text-xl text-gray-300 mb-12">
                  With over 10 years of proven expertise, A2Z Signs helps businesses of all sizes elevate their brand visibility. We design, fabricate, and install bold custom signs, LED displays, and illuminated signage with precision and passion. so your brand stands out, commands attention, and dominates the competition. Partner with us today and let your signage do the talking!
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-6">
                  <Link to="/contact" className="px-10 py-5 bg-brand-cyan text-brand-dark font-black text-xl rounded-full hover:bg-white transition-all duration-300 shadow-[0_0_30px_rgba(0,198,255,0.4)] hover:scale-105 flex items-center justify-center gap-2">
                    Get Your Free Quote <ArrowRight size={20} />
                  </Link>
                  <Link to="/portfolio" className="px-10 py-5 bg-transparent border-2 border-white text-white font-bold text-xl rounded-full hover:bg-white hover:text-brand-dark transition-all duration-300">
                    View Our Work
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </>
  );
};

export default Home;